if (!customElements.get('complete-the-set')) {
  customElements.define(
    'complete-the-set',
    class CompleteTheSet extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.variants = JSON.parse(this.querySelector('[data-completing-variants]').textContent);
        this.mainOptions = JSON.parse(this.querySelector('[data-main-options]').textContent);
        this.pills = this.querySelectorAll('.complete-set__pill');
        this.variantInput = this.querySelector('[data-complete-set-variant-id]');
        this.priceContainer = this.querySelector('[data-complete-set-price]');
        this.unavailableMsg = this.querySelector('[data-complete-set-unavailable]');
        this.mediaContainer = this.querySelector('[data-complete-set-media]');
        this.toggle = this.querySelector('[data-complete-set-toggle]');
        this.body = this.querySelector('[data-complete-set-body]');
        this.bundleEnabled = false;
        this.syncLocked = false;

        // Parse variant images map
        try {
          this.variantImages = JSON.parse(this.querySelector('[data-completing-images]').textContent);
        } catch (e) {
          console.warn('Complete the Set: could not parse variant images', e);
          this.variantImages = {};
        }

        // CTA button toggle handler
        this.toggle.addEventListener('click', () => {
          this.bundleEnabled = !this.bundleEnabled;
          if (this.bundleEnabled) this.syncLocked = true;
          this.toggle.classList.toggle('active', this.bundleEnabled);
          this.toggle.innerHTML = this.bundleEnabled
            ? '<svg class="complete-set__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg><span>Remove</span>'
            : '<svg class="complete-set__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg><span>Add</span>';
          if (this.body) this.body.classList.toggle('active', this.bundleEnabled);
        });

        // Attach form interceptor eagerly so it is ready when toggle is checked
        this.interceptFormSubmit();

        // Pill click handlers
        this.pills.forEach((pill) => {
          pill.addEventListener('click', () => {
            const group = pill.closest('[data-option-name]');
            group.querySelectorAll('.complete-set__pill').forEach((p) => p.classList.remove('selected'));
            pill.classList.add('selected');
            this.updateCompletingVariant();
          });
        });

        // Listen for main product variant changes
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onMainVariantChange.bind(this));

        // Initial sync
        this.syncWithMainProduct();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      syncWithMainProduct() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantJson = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (!variantJson) return;

        try {
          const variant = JSON.parse(variantJson.textContent);
          if (variant && variant.options) {
            this.matchOptions(variant.options);
          }
        } catch (e) {
          // Variant data not available yet
        }
      }

      onMainVariantChange({ data: { sectionId, variant } }) {
        if (this.syncLocked) return;
        if (sectionId !== this.sectionId) return;
        if (!variant || !variant.options) return;
        this.matchOptions(variant.options);
      }

      matchOptions(mainOptionValues) {
        this.querySelectorAll('[data-option-name]').forEach((group) => {
          const optionName = group.dataset.optionName;
          const mainIndex = this.mainOptions.indexOf(optionName);
          if (mainIndex === -1 || !mainOptionValues[mainIndex]) return;

          const matchValue = mainOptionValues[mainIndex];
          const pill = group.querySelector(`.complete-set__pill[data-value="${CSS.escape(matchValue)}"]`);
          if (pill) {
            group.querySelectorAll('.complete-set__pill').forEach((p) => p.classList.remove('selected'));
            pill.classList.add('selected');
          }
        });

        this.updateCompletingVariant();
      }

      getSelectedOptions() {
        const options = [];
        this.querySelectorAll('[data-option-name]').forEach((group) => {
          const selected = group.querySelector('.complete-set__pill.selected');
          options.push(selected ? selected.dataset.value : '');
        });
        return options;
      }

      updateCompletingVariant() {
        const selectedOptions = this.getSelectedOptions();

        const variant = this.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (variant) {
          this.variantInput.value = variant.id;
          this.currentVariant = variant;
          this.updatePrice(variant);
          this.updateImage(variant);
          this.setUnavailable(false);
        } else {
          this.currentVariant = null;
          this.setUnavailable(true);
        }
      }

      updatePrice(variant) {
        if (!this.priceContainer) return;

        const formatMoney = (cents) => {
          const amount = (cents / 100).toFixed(2);
          return window.Shopify?.currency?.active
            ? `${amount} ${window.Shopify.currency.active}`
            : `$${amount}`;
        };

        let priceHtml = `<span class="price-item price-item--regular">${formatMoney(variant.price)}</span>`;

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml =
            `<span class="price-item price-item--sale">${formatMoney(variant.price)}</span>` +
            ` <s class="price-item price-item--regular">${formatMoney(variant.compare_at_price)}</s>`;
        }

        this.priceContainer.innerHTML = priceHtml;
      }

      updateImage(variant) {
        if (!this.mediaContainer) return;

        // Try exact variant match first
        let imageData = this.variantImages[variant.id];

        // Fallback: in most stores only one variant per color has an image
        // assigned (e.g. Blue/S has image, Blue/M doesn't). Find any variant
        // sharing the same first option value (typically color) that has an image.
        if (!imageData) {
          for (const v of this.variants) {
            if (this.variantImages[v.id] && v.options[0] === variant.options[0]) {
              imageData = this.variantImages[v.id];
              break;
            }
          }
        }

        if (!imageData) return;

        const img = this.mediaContainer.querySelector('img');
        if (img) {
          img.src = imageData.src;
          img.srcset = imageData.srcset;
          img.alt = imageData.alt;
        }
      }

      setUnavailable(unavailable) {
        if (this.unavailableMsg) {
          this.unavailableMsg.classList.toggle('visible', unavailable);
        }
      }

      interceptFormSubmit() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        this.productFormEl = productInfo.querySelector('product-form');
        if (!this.productFormEl || !this.productFormEl.form) return;

        this.productFormEl.form.addEventListener(
          'submit',
          (evt) => {
            if (!this.bundleEnabled) return; // Let original handler run
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.handleBundleAdd();
          },
          { capture: true }
        );
      }

      renderBundleCart(cart, response) {
        const isNotification = cart.tagName === 'CART-NOTIFICATION';
        const items = response.items || [];

        if (isNotification && items.length > 1) {
          // Cart notification normally shows only ONE item (matched by key).
          // For bundle adds we need to show ALL added items.
          const sectionHtml = response.sections['cart-notification-product'];
          if (sectionHtml) {
            const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
            let productHtml = '';
            items.forEach((item) => {
              const el = doc.querySelector(`[id="cart-notification-product-${item.key}"]`);
              if (el) productHtml += el.outerHTML;
            });
            const container = document.getElementById('cart-notification-product');
            if (container) {
              // Wrap items in a block-level div so they stack vertically
              // without modifying the container's flex styles (which would
              // persist and break subsequent single-item renders).
              container.innerHTML = `<div>${productHtml}</div>`;
              container.firstChild.querySelectorAll('.cart-item').forEach((item) => {
                item.style.display = 'flex';
                item.style.alignItems = 'flex-start';
              });
            }
          }

          // Update cart button and icon bubble normally
          ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
            const el = document.getElementById(id);
            if (el && response.sections[id]) {
              const doc = new DOMParser().parseFromString(response.sections[id], 'text/html');
              const inner = doc.querySelector('.shopify-section');
              if (inner) el.innerHTML = inner.innerHTML;
            }
          });

          if (cart.header) cart.header.reveal();
          cart.open();
        } else {
          // Cart drawer or single-item fallback — normalize keys and use standard render
          if (!response.key && items.length) {
            response.key = items[0].key;
            response.id = items[0].id;
          }
          cart.renderContents(response);
        }
      }

      handleBundleAdd() {
        const pf = this.productFormEl;
        const submitButton = pf.submitButton || pf.querySelector('[type="submit"]');
        const spinner = pf.querySelector('.loading__spinner');
        const formData = new FormData(pf.form);
        const mainVariantId = formData.get('id');
        const completingVariantId = this.variantInput.value;
        const quantity = parseInt(formData.get('quantity')) || 1;

        // Show loading state
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        // Clear previous errors
        if (typeof pf.handleErrorMessage === 'function') {
          pf.handleErrorMessage();
        }

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Build items array — use form quantity for both products
        const items = [
          { id: parseInt(mainVariantId), quantity: quantity },
        ];

        if (this.currentVariant && this.currentVariant.available) {
          items.push({ id: parseInt(completingVariantId), quantity: quantity });
        }

        const body = { items: items };

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'complete-the-set',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });

              if (typeof pf.handleErrorMessage === 'function') {
                pf.handleErrorMessage(response.description);
              }
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'complete-the-set',
              productVariantId: mainVariantId,
              cartData: response,
            });

            this.renderBundleCart(cart, response);
          })
          .catch((e) => {
            console.error('Complete the Set: cart add failed', e);
          })
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
            if (cart && cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          });
      }
    }
  );
}
